import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { useSettings } from "@/context/SettingsContext";
import SearchBar from "@/components/SearchBar";
import ListingCard from "@/components/ListingCard";
import { useSeo, organizationJsonLd } from "@/lib/seo";
import { BRAND_SIGNATURE, SIGNATURE_PILLARS } from "@/lib/brand";
import CorridorMap from "@/components/CorridorMap";
import WhoAreYou from "@/components/WhoAreYou";

export default function Home() {
  const { lang, L, t } = useLang();
  const { settings } = useSettings();
  const [suggestions, setSuggestions] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [experts, setExperts] = useState([]);
  const [insights, setInsights] = useState([]);

  useEffect(() => {
    api.get("/suggestions").then(({ data }) => setSuggestions(data)).catch(() => {});
    api.get("/listings", { params: { featured: true, limit: 6 } }).then(({ data }) => setFeatured(data)).catch(() => {});
    api.get("/experts").then(({ data }) => setExperts(data.slice(0, 3))).catch(() => {});
    api.get("/insights", { params: { limit: 3 } }).then(({ data }) => setInsights(data)).catch(() => {});
  }, []);

  const hero = settings.hero || {};
  const process = settings.process || {};
  const steps = process[`steps_${lang}`] || [];
  const human = settings.human || {};
  const ambition = settings.ambition || {};

  useSeo({
    title: hero[`positioning_${lang}`] || (lang === "fr" ? "Une plateforme internationale ancrée en Afrique" : "An international platform anchored in Africa"),
    description: hero[`promise_${lang}`] || "Connecter marchés, capitaux, projets et partenaires.",
    lang,
    jsonLd: organizationJsonLd(),
  });

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-tertiary bg-ivory" data-testid="home-hero">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 py-16 lg:grid-cols-[1.05fr_1fr] lg:gap-16 lg:px-10 lg:py-24">
          <div className="flex flex-col justify-center">
            <span className="mb-6 text-xs uppercase tracking-[0.3em] text-champagne animate-fade-up">
              {lang === "fr" ? "Des idées. Des partenaires. Un impact." : "Ideas. Partners. Impact."}
            </span>
            <h1 className="mb-6 font-serif text-4xl leading-[1.08] tracking-tight text-anthracite animate-fade-up sm:text-5xl" style={{ animationDelay: "80ms" }}>
              {lang === "fr" ? "Des projets vérifiés." : "Verified projects."}<br />
              <span className="text-champagne">{lang === "fr" ? "Des partenaires de confiance." : "Trusted partners."}</span><br />
              {lang === "fr" ? "Des opportunités réelles." : "Real opportunities."}
            </h1>
            <p className="mb-8 max-w-lg text-lg leading-relaxed text-anthracite/70 animate-fade-up" style={{ animationDelay: "160ms" }}>
              {lang === "fr"
                ? "RSTI connecte porteurs de projets, investisseurs et partenaires entre l'Afrique, l'Europe et l'Asie — et transforme des idées en solutions durables."
                : "RSTI connects project owners, investors and partners across Africa, Europe and Asia — turning ideas into lasting solutions."}
            </p>
            <div className="mb-8 flex flex-wrap gap-3 animate-fade-up" style={{ animationDelay: "220ms" }}>
              <Link to="/connect" data-testid="hero-cta-connect" className="btn-gold inline-flex items-center gap-2 px-8 py-4 text-sm font-semibold text-anthracite">
                {lang === "fr" ? "Confiez-nous votre besoin" : "Tell us your need"} <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/opportunites" data-testid="hero-cta-explore" className="btn-green inline-flex items-center gap-2 px-8 py-4 text-sm font-semibold text-ivory">
                {lang === "fr" ? "Explorer les opportunités" : "Explore opportunities"} <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="animate-fade-up" style={{ animationDelay: "280ms" }}>
              <SearchBar suggestions={suggestions} />
            </div>
          </div>
          <div className="flex items-center animate-fade-up" style={{ animationDelay: "120ms" }}>
           <div className="relative w-full pb-8">
            <div className="grain-overlay aspect-[3/2] w-full overflow-hidden">
              <img src="/hero-rsti.jpg" alt={lang === "fr" ? "Partenaires RSTI connectés entre Afrique, Europe et Asie" : "RSTI partners connected across Africa, Europe and Asia"} className="h-full w-full object-cover object-top" data-testid="hero-image" />
            </div>
            <div className="absolute bottom-0 left-6 right-6 flex items-center justify-between gap-4 border border-tertiary bg-white px-6 py-4 shadow-[0_8px_30px_rgba(0,0,0,0.08)]" data-testid="hero-corridor-badge">
              {["Afrique", "Europe", "Asie"].map((c, i) => (
                <span key={c} className="flex items-center gap-3">
                  {i > 0 && <span className="h-px w-6 bg-champagne" />}
                  <span className="font-serif text-lg text-anthracite">{lang === "fr" ? c : ["Africa", "Europe", "Asia"][i]}</span>
                </span>
              ))}
            </div>
           </div>
          </div>
        </div>
      </section>

      <WhoAreYou lang={lang} />

      {/* FOUR DOORS */}
      <section className="mx-auto max-w-7xl px-6 pt-24 pb-20 lg:px-10" data-testid="home-doors">
        <div className="mb-10 max-w-2xl">
          <span className="text-xs uppercase tracking-[0.3em] text-champagne">{lang === "fr" ? "Pourquoi vous êtes ici" : "Why you are here"}</span>
          <h2 className="mt-3 font-serif text-3xl tracking-tight text-anthracite sm:text-4xl">
            {lang === "fr" ? "Quatre chemins, un seul interlocuteur." : "Four paths, one trusted counterpart."}
          </h2>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { to: "/projets", Icon: Icons.Lightbulb, tone: "bg-[#1F3A5F]", fr: ["Trouvez des projets vérifiés", "Des opportunités concrètes dans des secteurs porteurs."], en: ["Find verified projects", "Concrete opportunities in high-potential sectors."] },
            { to: "/partenaires", Icon: Icons.Users, tone: "bg-[#1F5F4A]", fr: ["Trouvez votre futur partenaire d'affaires", "Investisseurs, fournisseurs, experts, institutions."], en: ["Find your next business partner", "Investors, suppliers, experts, institutions."] },
            { to: "/investissement", Icon: Icons.Coins, tone: "bg-[#8A6A1F]", fr: ["Accédez à des financements", "Des solutions adaptées à chaque étape de votre projet."], en: ["Access funding", "Solutions adapted to every stage of your project."] },
            { to: "/marches", Icon: Icons.Globe, tone: "bg-anthracite", fr: ["Explorez des opportunités dans plusieurs secteurs", "Agriculture, énergie, santé, technologie, immobilier, éducation…"], en: ["Explore opportunities across sectors", "Agriculture, energy, health, technology, real estate, education…"] },
          ].map((d, i) => {
            const [title, sub] = lang === "fr" ? d.fr : d.en;
            return (
              <Link key={d.to} to={d.to} data-testid={`home-door-${d.to.slice(1)}`} className={`group flex flex-col justify-between ${d.tone} p-7 text-ivory transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_12px_40px_rgba(0,0,0,0.15)] animate-fade-up`} style={{ animationDelay: `${i * 70}ms` }}>
                <div>
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-ivory/30">
                    <d.Icon className="h-5 w-5 text-ivory" strokeWidth={1.5} />
                  </span>
                  <h3 className="mt-6 font-serif text-xl leading-snug">{title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-ivory/70">{sub}</p>
                </div>
                <ArrowRight className="mt-8 h-5 w-5 text-ivory/60 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.5} />
              </Link>
            );
          })}
        </div>
      </section>

      {/* CORRIDORS */}
      <section className="border-y border-tertiary bg-sand" data-testid="home-corridors">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 py-20 lg:grid-cols-[1fr_1.1fr] lg:px-10 lg:py-24">
          <div className="flex flex-col justify-center">
            <span className="text-xs uppercase tracking-[0.3em] text-champagne">{lang === "fr" ? "Des ambitions sans frontières" : "Ambitions without borders"}</span>
            <h2 className="mt-3 font-serif text-3xl leading-tight tracking-tight text-anthracite sm:text-4xl">
              {lang === "fr" ? "Afrique · Europe · Asie : un pont opéré par RSTI." : "Africa · Europe · Asia: a bridge operated by RSTI."}
            </h2>
            <p className="mt-6 max-w-lg text-base leading-relaxed text-anthracite/70">
              {lang === "fr"
                ? "Ancrée en Afrique, RSTI relie les capitaux, les savoir-faire et les marchés de trois continents. Chaque connexion est qualifiée par notre équipe avant d'être proposée : vous avancez avec un interlocuteur qui connaît les deux rives."
                : "Anchored in Africa, RSTI links capital, know-how and markets across three continents. Every connection is qualified by our team before it is proposed: you move forward with a counterpart who knows both shores."}
            </p>
            <p className="mt-4 text-xs text-anthracite/50">
              {lang === "fr" ? "RSTI réduit l'incertitude sans jamais promettre l'absence de risque." : "RSTI reduces uncertainty without ever promising the absence of risk."}
            </p>
          </div>
          <div className="flex flex-col gap-4">
            <CorridorMap lang={lang} />
            <div className="grid gap-3 sm:grid-cols-3">
              {[
                { fr: ["Afrique", "Projets, ressources, marchés en croissance et talents à révéler."], en: ["Africa", "Projects, resources, growing markets and talent to reveal."] },
                { fr: ["Europe", "Capitaux, technologies, institutions et exigences de conformité."], en: ["Europe", "Capital, technology, institutions and compliance standards."] },
                { fr: ["Asie", "Industrie, sourcing, investissement et vitesse d'exécution."], en: ["Asia", "Industry, sourcing, investment and speed of execution."] },
              ].map((c, i) => {
                const [name, desc] = lang === "fr" ? c.fr : c.en;
                return (
                  <div key={name} className="border border-tertiary bg-white p-5 animate-fade-up" style={{ animationDelay: `${i * 90}ms` }} data-testid={`home-corridor-${name.toLowerCase()}`}>
                    <h3 className="font-serif text-xl text-anthracite"><span className="mr-2 text-champagne/50">0{i + 1}</span>{name}</h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-anthracite/60">{desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* SIGNATURE */}
      <section className="border-b border-tertiary bg-anthracite text-ivory" data-testid="home-signature">
        <div className="mx-auto max-w-7xl px-6 py-20 lg:px-10">
          <div className="mb-12 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <span className="text-xs uppercase tracking-[0.3em] text-champagne">{lang === "fr" ? "Notre signature" : "Our signature"}</span>
              <h2 className="mt-3 font-serif text-3xl text-ivory sm:text-4xl">{BRAND_SIGNATURE}</h2>
            </div>
            <p className="max-w-md text-sm leading-relaxed text-ivory/60">
              {lang === "fr"
                ? "Quatre temps qui structurent chaque intervention de RSTI, de l'identification d'une opportunité à son impact économique mesurable."
                : "Four steps that structure every RSTI engagement, from identifying an opportunity to its measurable economic impact."}
            </p>
          </div>
          <div className="grid gap-px bg-ivory/10 sm:grid-cols-2 lg:grid-cols-4">
            {SIGNATURE_PILLARS.map((p, i) => {
              const Icon = [Icons.Eye, Icons.LayoutGrid, Icons.Repeat, Icons.TrendingUp][i];
              return (
                <div key={p.key} className="group bg-anthracite p-8 transition-colors duration-300 hover:bg-black animate-fade-up" style={{ animationDelay: `${i * 80}ms` }} data-testid={`home-pillar-${p.key.toLowerCase()}`}>
                  <span className="font-serif text-4xl text-champagne/30">0{i + 1}</span>
                  <Icon className="mt-6 h-6 w-6 text-champagne" strokeWidth={1.5} />
                  <h3 className="mt-5 font-serif text-2xl text-ivory">{p.key}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-ivory/60">{lang === "fr" ? p.fr : p.en}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* TRUST */}
      <section className="mx-auto max-w-7xl px-6 py-20 lg:px-10" data-testid="home-trust">
        <div className="grid gap-px overflow-hidden border border-tertiary bg-tertiary sm:grid-cols-2 lg:grid-cols-4">
          {[
            { Icon: Icons.ShieldCheck, fr: ["Vérifié par RSTI", "Chaque fiche publiée a été qualifiée par notre équipe."], en: ["Verified by RSTI", "Every published listing has been qualified by our team."] },
            { Icon: Icons.Handshake, fr: ["Connexions qualifiées", "Investisseurs, experts, institutions : une mise en relation opérée, pas automatique."], en: ["Qualified connections", "Investors, experts, institutions: an operated introduction, not an automated one."] },
            { Icon: Icons.Globe, fr: ["Présence internationale", "Un pont actif entre l'Afrique, l'Europe et l'Asie."], en: ["International presence", "An active bridge between Africa, Europe and Asia."] },
            { Icon: Icons.Leaf, fr: ["Secteurs variés", "Agriculture, énergie, santé, technologie, immobilier, éducation."], en: ["Diverse sectors", "Agriculture, energy, health, technology, real estate, education."] },
          ].map((tItem, i) => {
            const [title, sub] = lang === "fr" ? tItem.fr : tItem.en;
            return (
              <div key={title} className="flex gap-4 bg-white p-7" data-testid={`home-trust-${i}`}>
                <tItem.Icon className="mt-0.5 h-6 w-6 shrink-0 text-champagne" strokeWidth={1.5} />
                <div>
                  <p className="font-medium text-anthracite">{title}</p>
                  <p className="mt-1 text-sm leading-relaxed text-anthracite/60">{sub}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* FEATURED */}
      {featured.length > 0 && (
        <section className="border-y border-tertiary bg-sand">
          <div className="mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-28">
            <div className="mb-12 flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
              <div className="max-w-2xl">
                <span className="text-xs uppercase tracking-[0.3em] text-champagne">{t("featured_title")}</span>
                <h2 className="mt-3 font-serif text-3xl tracking-tight text-anthracite sm:text-4xl">{t("featured_sub")}</h2>
              </div>
            </div>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {featured.map((l, i) => (
                <ListingCard key={l.id} listing={l} index={i} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* PROCESS */}
      {steps.length > 0 && (
        <section className="mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-28">
          <div className="mb-12 max-w-2xl">
            <span className="text-xs uppercase tracking-[0.3em] text-champagne">{t("process_title")}</span>
            <h2 className="mt-3 font-serif text-3xl tracking-tight text-anthracite sm:text-4xl">
              {lang === "fr" ? "Identifier → Déployer" : "Identify → Deploy"}
            </h2>
          </div>
          <div className="grid grid-cols-2 gap-px overflow-hidden border border-tertiary bg-tertiary sm:grid-cols-3 lg:grid-cols-6">
            {steps.map((s, i) => (
              <div key={i} className="bg-white p-6">
                <span className="font-serif text-2xl text-champagne">{String(i + 1).padStart(2, "0")}</span>
                <p className="mt-2 text-sm font-medium text-anthracite">{s}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* HUMAN */}
      <section className="border-y border-tertiary bg-anthracite text-ivory">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 py-20 lg:grid-cols-[1fr_1fr] lg:px-10 lg:py-28">
          <div className="flex flex-col justify-center">
            <span className="mb-4 text-xs uppercase tracking-[0.3em] text-champagne">{t("experts_title")}</span>
            <h2 className="mb-6 font-serif text-3xl leading-tight tracking-tight sm:text-4xl">
              {human[`title_${lang}`] || t("experts_sub")}
            </h2>
            <p className="max-w-md text-base leading-relaxed text-ivory/70">{human[`body_${lang}`]}</p>
            {ambition[`text_${lang}`] && (
              <p className="mt-8 border-l-2 border-champagne pl-5 font-serif text-xl italic text-ivory/90">
                “{ambition[`text_${lang}`]}”
              </p>
            )}
          </div>
          <div className="grid gap-4">
            {experts.length === 0 && (
              <div className="border border-ivory/10 p-6 text-sm text-ivory/50">
                {lang === "fr" ? "Les experts RSTI seront présentés ici." : "RSTI experts will be featured here."}
              </div>
            )}
            {experts.map((e) => (
              <div key={e.id} className="flex items-center gap-4 border border-ivory/10 p-5">
                {e.photo ? (
                  <img src={e.photo} alt={e.name} className="h-14 w-14 rounded-full object-cover" />
                ) : (
                  <div className="flex h-14 w-14 items-center justify-center rounded-full bg-champagne/20 font-serif text-lg text-champagne">
                    {e.name?.charAt(0)}
                  </div>
                )}
                <div>
                  <p className="font-medium text-ivory">{e.name}</p>
                  <p className="text-sm text-ivory/50">{L(e, "role")}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INSIGHTS */}
      {insights.length > 0 && (
        <section className="mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-28">
          <div className="mb-12 max-w-2xl">
            <span className="text-xs uppercase tracking-[0.3em] text-champagne">{t("nav_insights")}</span>
            <h2 className="mt-3 font-serif text-3xl tracking-tight text-anthracite sm:text-4xl">{t("insights_title")}</h2>
          </div>
          <div className="grid gap-6 md:grid-cols-3">
            {insights.map((p, i) => (
              <Link key={p.id} to={`/insights/${p.slug}`} className="group animate-fade-up" style={{ animationDelay: `${i * 60}ms` }}>
                {p.cover_image && (
                  <div className="mb-4 aspect-[16/10] overflow-hidden bg-sand">
                    <img src={p.cover_image} alt={L(p, "title")} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" />
                  </div>
                )}
                <h3 className="font-serif text-xl text-anthracite group-hover:text-champagne">{L(p, "title")}</h3>
                <p className="mt-2 text-sm text-anthracite/60 line-clamp-2">{L(p, "excerpt")}</p>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-6 pb-24 lg:px-10">
        <div className="grain-overlay flex flex-col items-start justify-between gap-8 border border-tertiary bg-sand p-10 lg:flex-row lg:items-center lg:p-16">
          <div className="max-w-xl">
            <h2 className="font-serif text-3xl tracking-tight text-anthracite sm:text-4xl">{t("connect_title")}</h2>
            <p className="mt-3 text-anthracite/70">{t("connect_sub")}</p>
          </div>
          <Link
            to="/connect"
            data-testid="home-connect-cta"
            className="inline-flex items-center gap-2 bg-anthracite px-8 py-4 text-sm font-medium text-ivory transition-colors duration-300 hover:bg-black"
          >
            {t("cta_connect")} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
